/**
 * The viewer's recents log for every mount, moved from the shell's `services/recents-service.ts`
 * (kernel apps, WS-14 section 3.6; layered shell surfaces, WS-8 section 3.7, R-7, R-22).
 *
 * One recorder, many readers. The log algebra is the R-7 contract in `@zion/sdk-contracts`; the
 * device mirror is `recents-store.ts`. Nothing in `record` throws, and the controller reconciles
 * only on a read that resolved.
 *
 * The mirror key carries no identity, so the controller records whose log the mirror holds under
 * `STORAGE_KEYS.recentsOwner`, as `bindSavedStateOwner` does for saved state. On a change of
 * identity it empties a mirror that another identity owns before it merges, so one user's app
 * opens never reach another user's view or `home-recents` row (S2 WS-8 architecture-7,
 * security-5). Another tab can rebind the shared mirror to its own user at any time while this
 * tab's session stays unchanged, so every mirror read and write asks for the owner again.
 */
import {
  EMPTY_RECENTS_LOG,
  appendRecentOpen,
  mergeRecentsLogs,
  parseRecentsLog,
  recentsLogsEqual,
  type RecentsLog,
} from '@zion/sdk-contracts';
import type { SurfaceRecentsService } from '@zion/sdk-surfaces/v1';
import {
  createListenerSet,
  type MountScope,
  type ServiceClock,
  type ServiceSession,
  type ServiceSessionSnapshot,
} from './kit.js';
import { RECENTS_NAMESPACE, type PreferencesStore } from './preferences.js';
import { readRecentsLog, writeRecentsLog } from './recents-store.js';
import { STORAGE_KEYS } from './storage-keys.js';

/** The owner value of `snapshot`: `null` while it hydrates, `''` signed out, else `[userId, orgId]`. */
function mirrorOwnerOf(snapshot: ServiceSessionSnapshot): string | null {
  if (snapshot.status === 'hydrating') return null;
  return snapshot.status === 'authenticated' ? JSON.stringify([snapshot.userId, snapshot.orgId]) : '';
}

/**
 * Make the mirror belong to `owner`.
 *
 * @returns `true` when the mirror now holds only entries of `owner`: the recorded owner was
 *   `owner` or absent, or the call removed the mirror. `false` when storage refused, so the
 *   caller must treat the device side as empty.
 *
 * When the recorded owner is present and differs, the call removes the mirror key. Then it records
 * `owner`. An absent owner adopts the mirror as it is. It never throws.
 */
function bindMirrorOwner(owner: string): boolean {
  try {
    const recorded = localStorage.getItem(STORAGE_KEYS.recentsOwner);
    if (recorded !== null && recorded !== owner) localStorage.removeItem(STORAGE_KEYS.recents);
    localStorage.setItem(STORAGE_KEYS.recentsOwner, owner);
    return true;
  } catch {
    return false;
  }
}

/**
 * `true` when the recorded mirror owner is `owner` at this moment. A storage that refuses the read
 * answers `false`. It never throws.
 */
function mirrorOwnedBy(owner: string): boolean {
  try {
    return localStorage.getItem(STORAGE_KEYS.recentsOwner) === owner;
  } catch {
    return false;
  }
}

/**
 * The document-wide recents owner.
 *
 * - `current()` returns the log, starting at the device mirror before any fetch.
 * - Once the session is authenticated it reads `home-recents` through the store. Only a non-null
 *   result reconciles: the server log and the mirror merge at one `clock.now()`, the mirror is
 *   rewritten, subscribers hear once when the log changed, and `{ log }` is written back only
 *   when the merge differs from the server log. A failed read reconciles nothing and writes
 *   nothing, so the stored row survives.
 * - Once bound to an identity, every mirror read and write first checks that the recorded owner
 *   is still this tab's identity. When another tab has rebound the mirror to another identity,
 *   the controller reads its in-memory log in place of the mirror and leaves the mirror as it
 *   is. So `record` and the reconcile merge never carry, show or `PATCH` that identity's entries,
 *   and this tab never writes its own entries under the other identity's owner. Before the first
 *   bind (while the session hydrates) the mirror is the buffer, and the bind decides its owner.
 * - `record(appId)` appends at one `clock.now()`, rewrites the mirror and notifies. Before the
 *   reconcile the mirror is the buffer and nothing is sent; after it, the merge is written back
 *   too. A blank or oversized slug records nothing. It never throws.
 * - A change of `status`, `userId` or `orgId` resets the reconcile. When the new status is
 *   `anonymous` or `authenticated`, it binds the mirror to the new identity: a mirror that
 *   another identity owns is emptied, and a mirror whose owner storage cannot confirm counts as
 *   empty. `current()` then returns that device log, and subscribers hear once when it changed.
 *   When authenticated, it reads again. The merge never carries another identity's entries.
 * - `view(scope)` has `current` and `subscribe` only, so a surface cannot record. Its
 *   subscriptions end with the mount, and after the mount ends both members throw `disposed`.
 * - `dispose()` stops the session subscription and drops every subscriber; a later `record` or
 *   read answer changes nothing.
 */
export interface RecentsController {
  current(): RecentsLog;
  record(appId: string): void;
  subscribe(listener: () => void): () => void;
  view(scope: MountScope): SurfaceRecentsService;
  dispose(): void;
}

/**
 * Build the controller.
 *
 * @param prefs The document's preferences store; the controller is the one caller of its
 *   unchecked `read` and `write`, for {@link RECENTS_NAMESPACE}.
 * @param session This origin's session port.
 * @param clock The clock each entry's `t` comes from.
 */
export function createRecentsController(
  prefs: PreferencesStore,
  session: ServiceSession,
  clock: ServiceClock,
): RecentsController {
  const listeners = createListenerSet();
  let log: RecentsLog = readRecentsLog();
  let serverLog: RecentsLog | null = null;
  // `undefined` until the first reconcile, so a controller built while hydrating records `null`.
  let identity: string | null | undefined = undefined;
  // `false` when storage could not confirm the mirror's owner for this identity. The in-memory
  // log, emptied at the bind, then stands in for the mirror.
  let mirrorOwned = true;
  let generation = 0;
  let disposed = false;

  // `identity` is a string once bound: then the recorded owner must still be this identity.
  const ownsMirror = (): boolean =>
    typeof identity === 'string' ? mirrorOwned && mirrorOwnedBy(identity) : mirrorOwned;

  const deviceLog = (): RecentsLog => (ownsMirror() ? readRecentsLog() : log);

  const publish = (next: RecentsLog): void => {
    if (ownsMirror()) writeRecentsLog(next);
    if (recentsLogsEqual(next, log)) return;
    log = next;
    listeners.notify();
  };

  const reconcile = (): void => {
    const key = mirrorOwnerOf(session.current());
    if (key === identity) return;
    identity = key;
    serverLog = null;
    generation += 1;
    if (key === null) return;
    mirrorOwned = bindMirrorOwner(key);
    const device = mirrorOwned ? readRecentsLog() : EMPTY_RECENTS_LOG;
    if (!recentsLogsEqual(device, log)) {
      log = device;
      listeners.notify();
    }
    if (key === '') return;
    const captured = generation;
    void prefs.read(RECENTS_NAMESPACE).then((row) => {
      if (disposed || captured !== generation || row === null) return;
      const server = parseRecentsLog(row);
      const merged = mergeRecentsLogs(server, deviceLog(), clock.now());
      serverLog = merged;
      publish(merged);
      if (!recentsLogsEqual(merged, server)) prefs.write(RECENTS_NAMESPACE, { log: merged.log });
    });
  };

  const stopSession = session.subscribe(reconcile);
  reconcile();

  return {
    current: () => log,
    record(appId) {
      if (disposed) return;
      const now = clock.now();
      const mirror = deviceLog();
      const next = appendRecentOpen(mirror, appId, now);
      if (next === mirror) return;
      publish(next);
      if (serverLog === null) return;
      const merged = mergeRecentsLogs(serverLog, next, now);
      serverLog = merged;
      prefs.write(RECENTS_NAMESPACE, { log: merged.log });
    },
    subscribe: (listener) => listeners.add(listener),
    view(scope) {
      return scope.guard({
        current: () => log,
        subscribe: (listener: () => void) => {
          const remove = listeners.add(listener);
          scope.own(remove);
          return remove;
        },
      });
    },
    dispose() {
      disposed = true;
      stopSession();
      listeners.clear();
    },
  };
}
